import React, { useState } from 'react';
import {
    Box,
    Button,
    Collapse,
    Divider,
    Grid,
    InputBase,
    MenuItem,
    TextField,
    Typography,
    useTheme,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AiCueField from './AiCueField';
import { cueLabelSx, getTokens, metaSx } from './aiAgentTheme';

const VOICES = ['sage', 'alloy', 'ash', 'ballad', 'coral', 'echo', 'shimmer', 'verse'];
const MODELS = ['gpt-realtime', 'gpt-realtime-2.1', 'gpt-realtime-2.1-mini'];

/**
 * Scenár a povaha asistenta. Nastavuje sa raz za čas, kdežto volá sa denne –
 * preto je celá sekcia zbalená a až pod záznamom hovorov.
 */
const AiAgentSettings = ({ config, setField, saving, onSave }) => {
    const theme = useTheme();
    const tokens = getTokens(theme.palette.mode === 'dark', theme.palette.primary.main);
    const [open, setOpen] = useState(false);

    return (
        <Box sx={{ mt: 5, borderTop: `1px solid ${tokens.hairline}`, pt: 2.5 }}>
            <Box
                component="button"
                type="button"
                onClick={() => setOpen((v) => !v)}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    width: '100%',
                    appearance: 'none',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    p: 0,
                    textAlign: 'left',
                    '&:focus-visible': { outline: `2px solid ${tokens.accent}`, outlineOffset: 4 },
                }}
            >
                <ExpandMoreIcon
                    sx={{
                        fontSize: 20,
                        color: tokens.inkMuted,
                        transform: open ? 'rotate(180deg)' : 'none',
                        transition: 'transform .15s ease',
                    }}
                />
                <Box>
                    <Typography sx={{ fontSize: 17, fontWeight: 600, color: tokens.ink }}>
                        Scenár a povaha asistenta
                    </Typography>
                    <Typography sx={{ ...metaSx(tokens), mt: 0.25 }}>
                        Ako asistent hovorí a čo povie sám od seba. Platí pre všetky hovory, repliky o obrazovke
                        len pre tie v aplikácii.
                    </Typography>
                </Box>
            </Box>

            <Collapse in={open} unmountOnExit>
                <Box sx={{ maxWidth: 820, pt: 3 }}>
                    <Typography sx={{ color: tokens.inkMuted, fontSize: 13.5, mb: 3 }}>
                        Napíš vetu tak, ako ju má povedať. Prázdny riadok znamená, že v tom momente mlčí.
                    </Typography>

                    <AiCueField
                        when="Hneď po zdvihnutí"
                        value={config.greeting}
                        placeholder="Nepozdraví sa, čaká"
                        onChange={setField('greeting')}
                    />
                    <AiCueField
                        when="Keď zapne zdieľanie obrazovky"
                        value={config.onScreenStart}
                        placeholder="Nepovie nič"
                        onChange={setField('onScreenStart')}
                    />
                    <AiCueField
                        when="Keď sa obrazovka zmení"
                        value={config.onScreenChange}
                        placeholder="Na zmeny nereaguje"
                        onChange={setField('onScreenChange')}
                        hint={`Ozve sa pri zmene približne ${Math.round((config.changeRatio || 0.06) * 100)} % obrazovky`}
                        last
                    />

                    <Divider sx={{ my: 3, borderColor: tokens.hairline }} />

                    <Typography sx={{ ...cueLabelSx(tokens), mb: 1 }}>Povaha asistenta</Typography>
                    <InputBase
                        multiline
                        fullWidth
                        minRows={5}
                        value={config.instructions || ''}
                        onChange={setField('instructions')}
                        placeholder="Ako hovorí, čo smie a čo nie. Platí pre celý hovor."
                        sx={{
                            fontSize: 14.5,
                            lineHeight: 1.6,
                            color: tokens.ink,
                            border: `1px solid ${tokens.hairline}`,
                            p: 1.5,
                            '& textarea::placeholder': { color: tokens.inkMuted, opacity: 1, fontStyle: 'italic' },
                            '&.Mui-focused': { borderColor: tokens.accent },
                        }}
                    />

                    <Typography sx={{ ...cueLabelSx(tokens), mt: 3, mb: 1.5 }}>Menej časté nastavenia</Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                select
                                fullWidth
                                size="small"
                                label="Model"
                                value={config.model || ''}
                                onChange={setField('model')}
                                helperText="Obrazovku vidia len modely bez „mini“"
                            >
                                {MODELS.map((m) => (
                                    <MenuItem key={m} value={m}>
                                        {m}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                select
                                fullWidth
                                size="small"
                                label="Hlas"
                                value={config.voice || ''}
                                onChange={setField('voice')}
                            >
                                {VOICES.map((v) => (
                                    <MenuItem key={v} value={v}>
                                        {v}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Keď je dlho ticho"
                                value={config.onIdle || ''}
                                onChange={setField('onIdle')}
                                placeholder="Do ticha nehovorí"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                size="small"
                                type="number"
                                label="Ticho trvá aspoň (s)"
                                value={config.idleNarrateSeconds ?? 20}
                                onChange={setField('idleNarrateSeconds')}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                size="small"
                                type="number"
                                label="Pauza medzi vetami (s)"
                                value={config.minGapSeconds ?? 7}
                                onChange={setField('minGapSeconds')}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                size="small"
                                type="number"
                                inputProps={{ step: 0.01, min: 0.01, max: 1 }}
                                label="Citlivosť na zmenu obrazovky"
                                value={config.changeRatio ?? 0.06}
                                onChange={setField('changeRatio')}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                size="small"
                                type="number"
                                label="Asistent vstúpi do hovoru po (ms)"
                                value={config.joinDelayMs ?? 4000}
                                onChange={setField('joinDelayMs')}
                                helperText="Musí vstúpiť až po volanom"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                size="small"
                                type="number"
                                label="Pripomenúť zdieľanie po (s)"
                                value={config.remindShareSeconds ?? 0}
                                onChange={setField('remindShareSeconds')}
                                helperText="0 = nepripomína"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Text pripomienky"
                                value={config.remindShareText || ''}
                                onChange={setField('remindShareText')}
                            />
                        </Grid>
                    </Grid>

                    <Button
                        variant="contained"
                        disableElevation
                        onClick={onSave}
                        disabled={saving}
                        sx={{ mt: 3, borderRadius: 0, px: 3 }}
                    >
                        {saving ? 'Ukladám…' : 'Uložiť scenár'}
                    </Button>
                </Box>
            </Collapse>
        </Box>
    );
};

export default AiAgentSettings;
